import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Link } from '@client/components/Common/Link';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import clsx from 'clsx';
import { CarFront } from 'lucide-react';
import { Fragment } from 'react';
import { z } from 'zod';
import type { Metric } from '../~dashboard/sections/Claims/Fleet/fleet-data';
import { rentalActive, returnedThisWeek } from './fleet-portal-demo-data';
import { updateFleetScopeSelection } from './fleet-scope';
import { PortalMetricRow, PortalMetricTile, PortalSectionHeading } from './PortalMetrics';
import { claimRef, daysSince, offRoadDaysToneClass } from './portal-utils';
import { PortalScopeSelect, usePortalScope } from './usePortalScope';

const fleetSearchSchema = z.object({
    fleetClientId: z
        .string()
        .regex(/^[1-9]\d*$/)
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/fleet-portal/fleet')({
    staticData: {
        ui: {
            title: 'Fleet view',
            subtitle: 'Live vehicle list · rental figures are demo data',
            icon: CarFront,
        },
    },
    validateSearch: zodValidator(fleetSearchSchema),
    component: FleetPortalFleetPage,
});

type VehicleRow = RouterOutputs['fleetPortal']['listVehicles']['rows'][number];
type OpenClaim = NonNullable<VehicleRow['openClaim']>;
type OffRoadRow = VehicleRow & { openClaim: OpenClaim };

function FleetPortalFleetPage() {
    return (
        <PageController route={Route}>
            <FleetContent />
        </PageController>
    );
}

function FleetContent() {
    const orgId = useOrgId();
    const hideOnRoad = useFeatureFlag('hide-fleet-portal-on-road', false);
    const { trpc } = useTrpc();
    const { fleetClientId: fleetClientIdSearch } = Route.useSearch();
    const navigate = Route.useNavigate();
    const scope = usePortalScope(fleetClientIdSearch ? BigInt(fleetClientIdSearch) : undefined, () => {
        // Selected client left the caller's scope — drop it from the URL too so
        // the deep link stops naming a client the queries no longer honour.
        void navigate({ search: {}, replace: true });
    });
    const vehiclesQuery = trpc.fleetPortal.listVehicles.useQuery(
        { fleetClientId: scope.fleetClientId },
        { enabled: !scope.isLoading && !scope.error },
    );

    const setFleetClientId = (fleetClientId: bigint | undefined) => {
        updateFleetScopeSelection(fleetClientId, scope.setFleetClientId, navigate);
    };

    const scopeControl =
        scope.clients.length > 1 ? <PortalScopeSelect scope={{ ...scope, setFleetClientId }} /> : undefined;

    if (scope.error) {
        return (
            <div>
                <PageLayoutHeader />
                <InlineQueryError error={scope.error} onRetry={scope.refetch} />
            </div>
        );
    }

    if (vehiclesQuery.error) {
        return (
            <div>
                <PageLayoutHeader rightAddon={scopeControl} />
                <InlineQueryError error={vehiclesQuery.error} onRetry={() => void vehiclesQuery.refetch()} />
            </div>
        );
    }

    if (vehiclesQuery.isPending || scope.isLoading) {
        return (
            <div>
                <PageLayoutHeader rightAddon={scopeControl} />
                <Loading className="min-h-64" />
            </div>
        );
    }

    const { rows, totalFleet, truncated } = vehiclesQuery.data;
    // Off-road vehicles lead the page as a triage worklist, most days off road
    // first — they are what a fleet manager comes to check.
    const offRoadRows = rows
        .filter((row): row is OffRoadRow => row.openClaim !== null)
        .map((row) => ({ row, days: daysSince(row.openClaim.createdAt) ?? 0 }))
        .sort((a, b) => b.days - a.days)
        .map((entry) => entry.row);
    const onRoadRows = rows
        .filter((row) => row.openClaim === null)
        .sort((a, b) => a.registration.localeCompare(b.registration));
    const hasVisibleRows = offRoadRows.length > 0 || (!hideOnRoad && onRoadRows.length > 0);
    const showClientName = scope.clients.length > 1;

    const metrics: Metric[] = [
        { label: 'Total fleet', value: String(totalFleet), note: 'Active road-registered leases' },
        {
            label: 'Off road',
            value: String(offRoadRows.length),
            valueTone: offRoadRows.length > 0 ? 'warning' : 'default',
            note: 'Open claim in progress',
        },
        { label: 'Rental active', value: String(rentalActive), note: 'Replacement vehicles on hire', demo: true },
        {
            label: 'Returned this week',
            value: String(returnedThisWeek),
            note: 'Vehicles back with drivers',
            demo: true,
        },
    ];

    return (
        <div className="max-w-[1120px]">
            <PageLayoutHeader rightAddon={scopeControl} />

            <PortalMetricRow>
                {metrics.map((metric) => (
                    <PortalMetricTile key={metric.label} metric={metric} />
                ))}
            </PortalMetricRow>

            {!hasVisibleRows ? (
                <div className="mt-6 text-muted-foreground text-sm">
                    {rows.length === 0
                        ? truncated
                            ? 'No vehicles in the displayed results.'
                            : 'No vehicles in scope.'
                        : truncated
                          ? 'No off-road vehicles in the displayed results.'
                          : 'No vehicles currently off road.'}
                </div>
            ) : (
                <>
                    {offRoadRows.length > 0 && (
                        <>
                            <PortalSectionHeading label="Needs attention" count={offRoadRows.length} />
                            <div className="overflow-hidden rounded-lg border border-separator bg-card">
                                {offRoadRows.map((row) => (
                                    <OffRoadVehicleRow
                                        key={row.id.toString()}
                                        orgId={orgId}
                                        row={row}
                                        showClientName={showClientName}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                    {!hideOnRoad && onRoadRows.length > 0 && (
                        <>
                            <PortalSectionHeading label="On the road" count={onRoadRows.length} />
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                {onRoadRows.map((row) => (
                                    <OnRoadVehicleTile
                                        key={row.id.toString()}
                                        row={row}
                                        showClientName={showClientName}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}

function vehicleLabel(row: VehicleRow): string {
    return [row.make, row.description].filter(Boolean).join(' ') || '—';
}

const REPAIR_STAGES = ['Lodged', 'Assessment', 'Repair', 'Return'] as const;

/**
 * Current position in the fixed portal repair journey. The API exposes a free
 * `repairStage` string (set once repair work is under way) rather than a stage
 * enum, so: repair stage recorded → in repair; otherwise → in assessment.
 * "Return" is never current for an open claim — a returned vehicle's claim is
 * closed and the vehicle leaves this list.
 */
function currentStageIndex(claim: OpenClaim): number {
    return claim.repairStage ? 2 : 1;
}

function RepairStepper({ claim }: { claim: OpenClaim }) {
    const current = currentStageIndex(claim);

    return (
        <div className="hidden items-center lg:flex" title={claim.repairStage ?? undefined}>
            {REPAIR_STAGES.map((label, index) => (
                <Fragment key={label}>
                    {index > 0 && (
                        <span
                            className={clsx(
                                'relative -top-2 -mx-8 h-0.5 w-[52px]',
                                index < current ? 'bg-accent-500' : 'bg-separator',
                            )}
                        />
                    )}
                    <div className="flex w-[76px] flex-col items-center gap-1">
                        <span
                            className={clsx(
                                'relative z-10 h-2.5 w-2.5 rounded-full border-2',
                                index < current && 'border-accent-500 bg-accent-500',
                                index === current && 'border-accent-500 bg-card ring-[3px] ring-accent-100',
                                index > current && 'border-separator bg-card',
                            )}
                        />
                        <span
                            className={clsx(
                                'whitespace-nowrap font-semibold text-xs',
                                index < current && 'text-muted-foreground',
                                index === current && 'text-accent-600',
                                index > current && 'text-subtle-foreground',
                            )}
                        >
                            {label}
                        </span>
                    </div>
                </Fragment>
            ))}
        </div>
    );
}

function OffRoadVehicleRow({
    orgId,
    row,
    showClientName,
}: {
    orgId: bigint;
    row: OffRoadRow;
    showClientName: boolean;
}) {
    const days = daysSince(row.openClaim.createdAt);

    return (
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-6 border-separator border-t px-5 py-4 first:border-t-0 lg:grid-cols-[230px_1fr_auto_auto]">
            <div className="min-w-0">
                <div className="font-bold text-foreground">{row.registration}</div>
                <div className="text-muted-foreground text-sm">{vehicleLabel(row)}</div>
                {showClientName && <div className="mt-0.5 text-subtle-foreground text-xs">{row.clientName}</div>}
                {/* The stepper is lg-only — keep the stage readable on narrow screens. */}
                <div className="mt-0.5 text-subtle-foreground text-xs lg:hidden">
                    {row.openClaim.repairStage ?? 'Awaiting assessment'}
                </div>
            </div>
            <RepairStepper claim={row.openClaim} />
            <div className="min-w-[92px] text-right">
                <div className={clsx('font-bold text-2xl tabular-nums leading-none', offRoadDaysToneClass(days))}>
                    {days ?? '—'}
                </div>
                <div className="mt-0.5 text-subtle-foreground text-xs">days off road</div>
            </div>
            <div className="min-w-[110px] text-right text-sm">
                <Link
                    to="/console/$orgId/fleet-portal/claims/details/$claimId"
                    params={{ orgId, claimId: row.openClaim.id }}
                >
                    {claimRef(row.openClaim)}
                </Link>
            </div>
        </div>
    );
}

function OnRoadVehicleTile({ row, showClientName }: { row: VehicleRow; showClientName: boolean }) {
    return (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-separator bg-card px-3.5 py-3">
            <div className="min-w-0">
                <div className="truncate font-bold text-foreground text-sm">{row.registration}</div>
                <div className="mt-0.5 truncate text-subtle-foreground text-xs">{vehicleLabel(row)}</div>
                {showClientName && (
                    <div className="mt-0.5 truncate text-subtle-foreground text-xs">{row.clientName}</div>
                )}
            </div>
            <span aria-hidden className="h-2 w-2 flex-none rounded-full bg-success" />
            <span className="sr-only">No active claim</span>
        </div>
    );
}
